import React from 'react';
import styled from 'styled-components';
import { Badge, Box } from '@drone-js/core';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import DateChipContent, { DateChipType } from 'component/common/DateChip/DateChipContent';
import { useExperimentIsB } from 'component/common/Experiment/Experiment';
import { ExperimentId } from 'core/type/ExperimentId';

export interface Props {
    cms: CmsValue;
    type: DateChipType;
    date?: Date;
    isAvailable?: boolean;
    badge?: string;
    selected?: boolean;
    handleClick: (date?: Date) => void;
    buttonRef?: React.Ref<HTMLButtonElement>;
    isPopoverCalendarOpen?: boolean;
}

const BadgeStyled = styled(Badge)`
    position: absolute;
    bottom: 4px;
`;

const DateChip: React.FC<Props> = (props) => {
    const { cms, isAvailable, selected } = props;
    const defaultBtnRef = React.useRef<HTMLButtonElement>(null);
    const isPopoverCalendarOpen = props.isPopoverCalendarOpen ?? false;
    const isMoreDates = props.type === 'MoreDates';
    const isExperiment = useExperimentIsB(ExperimentId.ACT_4844);

    /* eslint-disable */
    const backgroundColor = isAvailable ? (selected ? 'product.primary.subtle' : 'base.surface') : 'base.surface';
    const borderColor = isAvailable ? (selected ? 'product.primary.strong' : 'base.default') : 'base.subtle';
    const textColor = isAvailable ? (selected ? 'product.primary' : 'base.default') : 'base.disabled';
    const dayNameColor = isAvailable ? (selected ? 'product.primary' : 'base.subtle') : 'base.disabled';
    /* eslint-enable */

    const handleClick = () => {
        return props.handleClick(props.date);
    };

    React.useEffect(() => {
        if (props.selected && defaultBtnRef.current && isPopoverCalendarOpen) {
            defaultBtnRef.current.focus();
        }
    }, [props.selected, isPopoverCalendarOpen]);

    return (
        <Box display="flex" flex={1} mx="4" justifyContent="center" position="relative" {...(isExperiment ? {} : { minWidth: '76px' })}>
            <Box
                as="button"
                interactive={isAvailable ? 'product.primary' : undefined}
                {...(isMoreDates ? { 'data-element-name': 'activities-more-dates-button' } : { 'data-element-name': 'activities-date-chip-button' })}
                data-testid={isMoreDates ? 'activities-more-dates-button' : 'date-chip-element'}
                backgroundColor={backgroundColor}
                borderColor={borderColor}
                textColor={textColor}
                display="flex"
                justifyContent="center"
                alignItems="center"
                whiteSpace="nowrap"
                borderRadius="8"
                borderStyle="solid"
                borderWidth="2"
                cursor={isAvailable ? 'pointer' : 'not-allowed'}
                disabled={!isAvailable}
                py="4"
                m="4"
                mb="12"
                minWidth="100%"
                // Please check MoreDatesChip when adjusting minHeight, too small minHeight will cause MoreDatesChip to be shorter than the other DateChips due to stacking flex Box inside Popover
                minHeight="105px"
                aria-pressed={selected}
                onClick={handleClick}
                ref={props.buttonRef || defaultBtnRef}
                aria-haspopup={isMoreDates ? 'dialog' : undefined}
                aria-controls={isMoreDates ? 'dialog' : undefined}
                aria-expanded={isMoreDates ? isPopoverCalendarOpen.toString() : undefined}
            >
                <DateChipContent cms={cms} type={props.type} date={props.date} dayNameColor={dayNameColor} />
            </Box>
            {props.badge && <BadgeStyled label={props.badge} contentAlignment="center" variant="primary.neutral-1" data-testid="date-chip-badge" />}
        </Box>
    );
};

DateChip.displayName = 'DateChip';
export default DateChip;
